import { formatDateString } from "common/utils/dates";
import { pluralize } from "common/utils/strings";
import type { SubmissionTypeVersion } from "../../../../../../generated/graphql";
import { spacing } from "../../../../../../stitches.config";
import { FlexRow } from "../../../../../Common/__styles__/Layout";
import { ActionButton } from "../../../../../Common/Buttons/ActionButton";
import Divider from "../../../../../Common/Divider";
import { Title } from "../../../../../Common/Typography";
import { SelectionCard } from "../../../../../Inputs/SelectionCard";
import { ColumnWrapper } from "../__styles__/SubmissionsBuilder";
import {
  SUBMISSIONS_BUILDER_MODE,
  useSubmissionsBuilderContext,
  useSubmissionsBuilderMode,
} from "../SubmissionsBuilderContext";

export function VersionHistory() {
  const { existingSubmissionType, selectedVersionId, selectVersion } =
    useSubmissionsBuilderContext();
  const [, setMode] = useSubmissionsBuilderMode();

  return (
    <ColumnWrapper>
      <FlexRow css={{ justifyContent: "space-between", alignItems: "center" }}>
        <Title size="default" type="semiBold">
          Version history
        </Title>
        <ActionButton
          icon="x"
          onClick={() => setMode(SUBMISSIONS_BUILDER_MODE.BUILD)}
          data-testid="close-version-history"
          aria-label="Close version history"
        />
      </FlexRow>
      <Divider margins={`0px -${spacing.l.value}`} />
      <SelectionCard.Group
        currentValue={selectedVersionId}
        handleSelection={selectVersion}
        direction="vertical"
        spacing="default"
      >
        {existingSubmissionType?.versions.map(version => (
          <SelectionCard.Item
            key={version.id}
            value={version.id}
            borderStyle="ghost"
            size="default"
          >
            <SelectionCard.CommonContent
              title={generateTitle({
                version,
                isCurrent:
                  version.id === existingSubmissionType?.currentVersion.id,
              })}
              description={generateDescription({
                version,
                isCurrent:
                  version.id === existingSubmissionType?.currentVersion.id,
              })}
            />
          </SelectionCard.Item>
        ))}
      </SelectionCard.Group>
    </ColumnWrapper>
  );
}

function generateTitle({
  version,
  isCurrent,
}: {
  version: Pick<SubmissionTypeVersion, "version" | "updatedAt">;
  isCurrent: boolean;
}): string {
  if (isCurrent) {
    return `Current version`;
  }

  return `Version ${version.version} · ${formatDateString({
    dateString: version.updatedAt,
    format: "MM D, YYYY H:MM",
  })}`;
}

function generateDescription({
  version,
  isCurrent,
}: {
  version: Pick<SubmissionTypeVersion, "submissionCount">;
  isCurrent: boolean;
}): string {
  const noun = pluralize({
    count: version.submissionCount,
    options: { singular: "record", plural: "records" },
  });

  return `${version.submissionCount} ${noun}${isCurrent ? " created" : ""}`;
}
